import { R7E_CmsAuth, LocalStoragePrefix } from '../cms/r7e_cms.js';

const COOKIE_PREFIX = (LocalStoragePrefix + 'auth_').replace('.', '_');
const CROSS_ORIGIN_MESSAGE_TYPE = COOKIE_PREFIX.toUpperCase() + '_SYNC';

/**
 * Domain-based authentication service that extends the base CMS auth
 * with domain cookies and cross-origin messaging for LocalStorage synchronization
 */
class R7E_DomainAuth extends R7E_CmsAuth {
    constructor() {
        super();
        this.domain = process.env.VUE_APP_R7E_AUTH_DOMAIN || window.location.hostname;
        this.isCrossOriginMessagingEnabled = true;
        this.messageHandlers = new Map();
        this.setupCrossOriginMessaging();
    }

    /**
     * Setup cross-origin messaging for LocalStorage synchronization
     */
    setupCrossOriginMessaging() {
        if (!this.isCrossOriginMessagingEnabled) return;

        // Listen for cross-origin messages
        window.addEventListener('message', (event) => {
            this.handleCrossOriginMessage(event);
        });

        // Listen for storage events (same-origin)
        window.addEventListener('storage', (event) => {
            this.handleStorageEvent(event);
        });
    }

    /**
     * Handle cross-origin messages for auth synchronization
     */
    handleCrossOriginMessage(event) {
        // Security: Only accept messages from trusted origins
        if (!this.isTrustedOrigin(event.origin)) {
            return;
        }

        const { type, data } = event.data || {};
        
        if (type === CROSS_ORIGIN_MESSAGE_TYPE) {
            this.syncAuthData(data);
        }
    }

    /**
     * Handle storage events for same-origin synchronization
     */
    handleStorageEvent(event) {
        if (event.key && event.key.startsWith(LocalStoragePrefix)) {
            // Notify other tabs/windows about the change
            this.broadcastAuthChange(event.key, event.newValue, event.oldValue);
        }
    }

    /**
     * Check if origin is trusted for cross-origin messaging
     */
    isTrustedOrigin(origin) {
        try {
            const originUrl = new URL(origin);
            const currentUrl = new URL(window.location.origin);
            
            // Same domain or subdomain
            return originUrl.hostname === currentUrl.hostname || 
                   originUrl.hostname.endsWith('.' + currentUrl.hostname) ||
                   currentUrl.hostname.endsWith('.' + originUrl.hostname);
        } catch (e) {
            return false;
        }
    }

    /**
     * Sync authentication data from cross-origin message
     */
    syncAuthData(data) {
        if (!data || typeof data !== 'object') return;

        const { action, key, value } = data;
        
        switch (action) {
            case 'setItem':
                if (key && key.startsWith(LocalStoragePrefix)) {
                    localStorage.setItem(key, value);
                    this.updateLocalAuthState();
                }
                break;
            case 'removeItem':
                if (key && key.startsWith(LocalStoragePrefix)) {
                    localStorage.removeItem(key);
                    this.updateLocalAuthState();
                }
                break;
            case 'clear':
                this.clearLocalAuthData();
                break;
        }
    }

    /**
     * Update local authentication state from localStorage
     */
    updateLocalAuthState() {
        // console.log('R7E_DomainAuth, updateLocalAuthState', this.authData);
        this.authData.username = localStorage.getItem(LocalStoragePrefix + 'username') || '';
        this.authData.name = localStorage.getItem(LocalStoragePrefix + 'name') || '';
        this.authData.email = localStorage.getItem(LocalStoragePrefix + 'email') || '';
        this.authData.token = localStorage.getItem(LocalStoragePrefix + 'token') || '';
        this.authData.userid = localStorage.getItem(LocalStoragePrefix + 'userid') || '';
        this.authData.refreshKey = localStorage.getItem(LocalStoragePrefix + 'refreshKey') || '';

        const pendingRegistrationsStr = localStorage.getItem(LocalStoragePrefix + 'pendingRegistrations') || '{}';
        this.authData.pendingRegistrations = JSON.parse(pendingRegistrationsStr);

        const rolesStr = localStorage.getItem(LocalStoragePrefix + 'roles') || '[]';
        this.authData.roles = JSON.parse(rolesStr);
        
        if (this.authData.roles && typeof this.authData.roles === 'object' && !Array.isArray(this.authData.roles)) {
            this.authData.roles = Object.keys(this.authData.roles).filter(k => this.authData.roles[k]);
        }
        
        this.notifyObservers(this.authData);
    }

    /**
     * Clear local authentication data
     */
    clearLocalAuthData() {
        this.authData.clear();
        this.notifyObservers(this.authData);
    }

    /**
     * Broadcast authentication changes to other windows/tabs
     */
    // eslint-disable-next-line no-unused-vars
    broadcastAuthChange(key, newValue, oldValue) {
        if (!this.isCrossOriginMessagingEnabled) return;

        const message = {
            type: CROSS_ORIGIN_MESSAGE_TYPE,
            data: {
                action: newValue ? 'setItem' : 'removeItem',
                key: key,
                value: newValue
            }
        };

        // Broadcast to all windows (including cross-origin iframes)
        window.postMessage(message, '*');
    }

    /**
     * Set domain cookie for authentication
     */
    setDomainCookie(name, value, options = {}) {
        // if (DEBUG_MODE) {
        //     console.log('R7E_DomainAuth, setDomainCookie', name, value, options);       
        // }
        const cookieName = COOKIE_PREFIX + name;
        const cookieValue = encodeURIComponent(value);
        
        // Default cookie options
        const defaultOptions = {
            domain: this.domain,
            path: '/',
            secure: window.location.protocol === 'https:',
            sameSite: 'Lax',
            maxAge: 7 * 24 * 60 * 60 // 7 days
        };

        const cookieOptions = { ...defaultOptions, ...options };      
        let cookieString = `${cookieName}=${cookieValue}`;
        if (cookieOptions.maxAge) { cookieString += `; Max-Age=${cookieOptions.maxAge}`; }
        if (cookieOptions.domain && cookieOptions.domain !== 'localhost') { cookieString += `; Domain=${cookieOptions.domain}`; }
        if (cookieOptions.path) { cookieString += `; Path=${cookieOptions.path}`; }
        if (cookieOptions.secure) { cookieString += `; Secure`; }
        if (cookieOptions.sameSite) { cookieString += `; SameSite=${cookieOptions.sameSite}`; }

        // if (DEBUG_MODE) {
            // console.log('R7E_DomainAuth, setDomainCookie', cookieString);
        // }

        document.cookie = cookieString;
    }

    /**
     * Get domain cookie value
     */
    getDomainCookie(name) {
        const cookieName = COOKIE_PREFIX + name;
        const cookies = document.cookie.split(';');
        
        for (let cookie of cookies) {
            const [key, value] = cookie.trim().split('=');
            if (key === cookieName) {
                return decodeURIComponent(value);
            }
        }
        
        return null;
    }

    /**
     * Remove domain cookie
     */
    removeDomainCookie(name) {
        this.setDomainCookie(name, '', { maxAge: 0 });
    }

    /**
     * Enhanced updateAccountStorage that also sets domain cookies
     */
    updateAccountStorage(obj) {
        // console.log("R7E_DomainAuth, updateAccountStorage", obj);

        const age_7days = 7 * 24 * 60 * 60; // 7 days
        const age_30days = 30 * 24 * 60 * 60; // 30 days
       
        // Also set domain cookies for cross-domain access
        for (const key of obj.getKeys()) {
            if (obj[key]) {
                if (key === 'refreshKey') {
                    this.setDomainCookie(key, obj[key], { maxAge: age_30days }); // 30 days
                } else {
                    if (typeof obj[key] === 'object') {
                        this.setDomainCookie(key, JSON.stringify(obj[key]), { maxAge: age_7days }); // 7 days
                    } else if (Array.isArray(obj[key])) {
                        if (obj[key].length > 0) {
                            this.setDomainCookie(key, JSON.stringify(obj[key]), { maxAge: age_7days }); // 7 days
                        } else {
                            this.setDomainCookie(key, '', { maxAge: age_7days }); // 7 days
                        }
                    } else {
                        this.setDomainCookie(key, obj[key], { maxAge: age_7days }); // 7 days
                    }
                }
            }
        }

        // Call parent method to update localStorage
        super.updateAccountStorage(obj);
    }

    /**
     * Enhanced loadCredentials that also checks domain cookies
     */
    loadCredentials() {
        // console.log("R7E_DomainAuth.loadCredentials");
        // First try to load from localStorage
        // const hasLocalStorage = super.loadCredentials();
        const hasLocalStorage = false
        
        // If no localStorage data, try to load from domain cookies
        if (!hasLocalStorage) {
            // console.log("R7E_DomainAuth.loadCredentials, no localStorage data, trying to load from domain cookies");
            const authKeys = this.authData.getKeys();
            const hasCookieToken = this.getDomainCookie('token') !== null;
            if (hasCookieToken) {
                for (const key of authKeys) {
                    const cookieValue = this.getDomainCookie(key);
                    if (cookieValue) {
                        if (key === 'email') {
                            this.authData.setEmail(cookieValue);
                        } else if (key === 'pendingRegistrations') {
                            this.authData.pendingRegistrations = JSON.parse(cookieValue) || {};
                        } else if (key === 'roles') {
                            this.authData.roles = JSON.parse(cookieValue) || [];
                        } else {
                            this.authData[key] = cookieValue;
                        }
                    }
                }
                
                if (!this.authData.roles || this.authData.roles.length === 0) {
                    this.authData.roles = [];
                }             
                // Sync back to localStorage
                this.updateAccountStorage(this.authData);
                this.notifyObservers(this.authData);
                if (this.authData.token) this.scheduleTokenRefresh();
                return true;
            }
        }       
        return hasLocalStorage;
    }

    /**
     * Enhanced clearCredentials that also clears domain cookies
     */
    async clearCredentials() {
        // console.log('R7E_DomainAuth, clearCredentials', this.baseAppType);
        // Clear domain cookies
        for (const key of this.authData.getKeys()) {
            this.removeDomainCookie(key);
        }
        // Call parent method to clear localStorage
        await super.clearCredentials();
        
        // Broadcast the clear action
        this.broadcastAuthChange('clear', null, null);
    }

    /**
     * Enhanced clearCredentialsSync that also clears domain cookies
     */
    clearCredentialsSync() {
        // console.log('R7E_DomainAuth, clearCredentialsSync');
        // Clear domain cookies
        for (const key of this.authData.getKeys()) {
            this.removeDomainCookie(key);
        }
        
        // Call parent method to clear localStorage
        super.clearCredentialsSync();
        
        // Broadcast the clear action
        this.broadcastAuthChange('clear', null, null);
    }

    /**
     * Get the configured domain for authentication
     */
    getAuthDomain() {
        return this.domain;
    }

    /**
     * Set the domain for authentication (useful for testing or dynamic configuration)
     */
    setAuthDomain(domain) {
        this.domain = domain;
    }

    /**
     * Enable or disable cross-origin messaging
     */
    setCrossOriginMessagingEnabled(enabled) {
        this.isCrossOriginMessagingEnabled = enabled;
    }
}

export { R7E_DomainAuth };
